import React, { useState, useEffect } from 'react';
import PrintableContent from './PrintableContent';
import { useParams, useNavigate } from 'react-router-dom';
import { firestore } from './firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { FaStar, FaRegStar, FaPencilAlt, FaTrash, FaPlus, FaCheck } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const normalizeNameForLookup = (name) => (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
const buildChildLookupName = (firstName, lastName) =>
  `${normalizeNameForLookup(firstName)}|${normalizeNameForLookup(lastName)}`;

function PersonDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [person, setPerson] = useState(null);
  const [editableAllergies, setEditableAllergies] = useState('');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editablePerson, setEditablePerson] = useState(null);

  useEffect(() => {
    const fetchPerson = async () => {
      try {
        const docRef = doc(firestore, 'students', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();

          let childBirthday;
          if (data.children && data.children[0] && data.children[0].dateOfBirth.toDate) {
            childBirthday = data.children[0].dateOfBirth.toDate();
          } else if (data.children && data.children[0] && typeof data.children[0].dateOfBirth === 'string') {
            childBirthday = new Date(data.children[0].dateOfBirth);
          } else {
            childBirthday = data.children[0].dateOfBirth;
          }

          const ageInMonths = calculateAgeInMonths(childBirthday);
          const childClass = getClassBasedOnAge(ageInMonths);

          const enquiryDate = data.enquiry.enquiryDate.toDate();
          const desiredEnrollmentDate = data.enquiry.desiredEnrollmentDate.toDate();

          const personData = {
            id: docSnap.id,
            children: {
              ...data.children[0],
              class: childClass,
              dateOfBirth: childBirthday,
              hasSiblingsEnrolled: data.children.hasSiblingsEnrolled,
              notes: data.children[0].notes || [],
              siblings: data.children[0].siblings || [],
            },
            parent: {
              contactMethod: data.parent.contactMethod,
              parentFirstName: data.parent.parentFirstName,
              parentLastName: data.parent.parentLastName,
              email: data.parent.email,
              phone: data.parent.phoneNumber || null,
            },
            enquiry: {
              desiredEnrollmentDate: desiredEnrollmentDate,
              enquiryDate: enquiryDate,
              status: data.enquiry.status,
              priority: data.enquiry.priority,
              whichWaitlist: data.enquiry.whichWaitlist,
            },
          };

          setPerson(personData);
          setEditablePerson(personData);
          setEditableAllergies(data.children[0].allergies);
        } else {
          console.error('No such document!');
        }
      } catch (error) {
        console.error('Error fetching person data:', error);
      }
    };

    if (id) {
      fetchPerson();
    }
  }, [id]);

  const calculateAgeInMonths = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    const yearsDifference = today.getFullYear() - birthDate.getFullYear();
    const monthsDifference = today.getMonth() - birthDate.getMonth();
    const daysDifference = today.getDate() - birthDate.getDate();

    let ageInMonths = yearsDifference * 12 + monthsDifference;
    if (daysDifference < 0) {
      ageInMonths--;
    }

    return ageInMonths;
  };

  const getClassBasedOnAge = (ageInMonths) => {
    switch (true) {
      case (ageInMonths >= 0 && ageInMonths < 12): // 0 to <12 months
        return "Cubs";
      case (ageInMonths >= 12 && ageInMonths < 18): // 12 to <18 months
        return "Teddies";
      case (ageInMonths >= 18 && ageInMonths < 24): // 18 to <24 months
        return "Koalas";
      case (ageInMonths >= 24 && ageInMonths < 36): // 24 to <36 months
        return "Pandas";
      case (ageInMonths >= 36 && ageInMonths < 48): // 36 to <48 months
        return "Preschool";
      case (ageInMonths >= 48 && ageInMonths < 60): // 48 to <60 months
        return "Pre-K";
      case (ageInMonths >= 60): // 60 months and older
        return "Kindergarten";
      default:
        return "Unknown"; // Added a default case for safety
    }
  };

  const onBack = () => {
    navigate('/waitlist');
  };

  const handleAllergiesChange = (e) => {
    setEditableAllergies(e.target.value);
  };

  const handleAllergiesSave = async () => {
    if (person) {
      const updatedChildren = { ...person.children, allergies: editableAllergies };
      const docRef = doc(firestore, 'students', person.id);

      await updateDoc(docRef, {
        children: [removeUndefined(updatedChildren)],
      });
      
      setPerson((prevPerson) => ({
        ...prevPerson,
        children: updatedChildren,
      }));
    }
  };

  const handleAddNote = () => {
    setAddingNote(true);
  };

  const handleNewNoteChange = (e) => {
    setNewNote(e.target.value);
  };

  const handleSaveNewNote = async () => {
    if (person && newNote.trim() !== '') {
      const updatedChildren = {
        ...person.children,
        notes: [...person.children.notes, { content: newNote, timestamp: new Date().toISOString() }],
      };

      const docRef = doc(firestore, 'students', person.id);
      await updateDoc(docRef, {
        children: [removeUndefined(updatedChildren)],
      });

      setPerson((prevPerson) => ({
        ...prevPerson,
        children: updatedChildren,
      }));
      setNewNote('');
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (index) => {
    if (person) {
      const updatedChildren = {
        ...person.children,
        notes: person.children.notes.filter((_, i) => i !== index),
      };

      const docRef = doc(firestore, 'students', person.id);
      await updateDoc(docRef, {
        children: [removeUndefined(updatedChildren)],
      });

      setPerson((prevPerson) => ({
        ...prevPerson,
        children: updatedChildren,
      }));
    }
  };

  const handlePriorityToggle = async () => {
    if (person) {
      const newPriorityStatus = !person.enquiry.priority;
      const docRef = doc(firestore, 'students', person.id);
      await updateDoc(docRef, {
        'enquiry.priority': newPriorityStatus,
      });
      setPerson((prevPerson) => ({
        ...prevPerson,
        enquiry: {
          ...prevPerson.enquiry,
          priority: newPriorityStatus,
        },
      }));
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  const handleEditClick = () => {
    setEditing(true);
  };

  const handleCancelClick = () => {
    setEditing(false);
    setEditablePerson(person);
  };

  const handleSaveClick = async () => {
    if (person && editablePerson) {
      console.log("Editable person data before saving:", editablePerson);
  
      const docRef = doc(firestore, 'students', person.id);
  
      // Ensure 'phoneNumber' is defined or set to null explicitly
      const parentData = {
        ...editablePerson.parent,
        phoneNumber: editablePerson.parent.phone
          ? editablePerson.parent.phone.trim() || null
          : null,
      };
  
      // Log parentData to verify the value of phoneNumber
      console.log("Parent data before saving:", parentData);
  
      const updatedData = {
        children: [removeUndefined(editablePerson.children)],
        parent: parentData,
        enquiry: {
          ...editablePerson.enquiry,
          desiredEnrollmentDate: editablePerson.enquiry.desiredEnrollmentDate
            ? new Date(editablePerson.enquiry.desiredEnrollmentDate)
            : null,
          enquiryDate: editablePerson.enquiry.enquiryDate
            ? new Date(editablePerson.enquiry.enquiryDate)
            : null,
        },
        childLookupName: buildChildLookupName(
          editablePerson.children.firstName,
          editablePerson.children.lastName
        ),
      };
  
      // Log the final object to confirm no undefined fields
      console.log("Updated data to save:", updatedData);
  
      try {
        await updateDoc(docRef, updatedData);
        setPerson(editablePerson);
        setEditing(false);
      } catch (error) {
        console.error("Error updating document:", error.message, error.stack);
      }
    }
  };
  
  

  const handleDateChange = (date, section, field) => {
    const updatedPerson = { ...editablePerson };
    updatedPerson[section][field] = date;
    setEditablePerson(updatedPerson);
  };

  const handleChange = (e, section, field) => {
    const updatedPerson = { ...editablePerson };
    if (section) {
      updatedPerson[section][field] = e.target.value;
    } else {
      updatedPerson[field] = e.target.value;
    }
    setEditablePerson(updatedPerson);
  };

  const handleDelete = async () => {
    const confirmation = window.confirm("Are you sure you want to delete this record?");
    if (confirmation) {
      const docRef = doc(firestore, 'students', id);
      await deleteDoc(docRef);
      navigate('/waitlist');
    }
  };

  const fetchSiblingId = async (firstName, lastName) => {
    try {
      const lookupName = buildChildLookupName(firstName, lastName);
      const indexedQuery = query(
        collection(firestore, 'students'),
        where('childLookupName', '==', lookupName)
      );
      const indexedSnapshot = await getDocs(indexedQuery);
      const indexedMatch = indexedSnapshot.docs.find((studentDoc) => studentDoc.id !== person?.id);
      if (indexedMatch) {
        return indexedMatch.id;
      }

      const querySnapshot = await getDocs(collection(firestore, 'students'));
      let foundSiblingId = null;
  
      querySnapshot.forEach((studentDoc) => {
        if (studentDoc.id === person?.id) {
          return;
        }

        const data = studentDoc.data();
        const child = data.children && data.children[0];
  
        if (child && child.firstName && child.lastName) {
          const docFirstName = normalizeNameForLookup(child.firstName);
          const docLastName = normalizeNameForLookup(child.lastName);
          const searchFirstName = normalizeNameForLookup(firstName);
          const searchLastName = normalizeNameForLookup(lastName);
  
          if (docFirstName === searchFirstName && docLastName === searchLastName) {
            foundSiblingId = studentDoc.id;
          }
        }
      });
  
      if (!foundSiblingId) {
        console.error(`No sibling found with name: ${firstName} ${lastName}`);
      }
      return foundSiblingId;
    } catch (error) {
      console.error('Error fetching sibling data:', error);
      return null;
    }
  };

  const handleSiblingClick = async (firstName, lastName) => {
    const siblingId = await fetchSiblingId(firstName, lastName);
    if (siblingId) {
      window.open(`/personDetail/${siblingId}`);
    } else {
      alert('No sibling found with the given name.');
    }
  };

  if (!person) {
    return <div className="text-center mt-4">Loading...</div>;
  }

  const sectionCardClass = "rounded-xl border border-gray-200 bg-white p-5 shadow-sm";
  const sectionTitleClass = "mb-4 text-xl font-semibold text-gray-800";
  const fieldLabelClass = "mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500";
  const fieldValueClass = "rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-gray-800";

  return (
    <div className="mx-auto mt-6 max-w-5xl px-4 pb-8">
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-lg sm:p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3 rounded-lg border border-blue-100 bg-white p-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {person.children.firstName} {person.children.lastName}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Parent: {person.parent.parentFirstName} {person.parent.parentLastName}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="rounded-full bg-blue-100 px-3 py-1 font-medium text-blue-800">
              {person.children.class}
            </span>
            <span className="rounded-full bg-indigo-100 px-3 py-1 font-medium text-indigo-800">
              {person.enquiry.whichWaitlist}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className={sectionCardClass}>
            <h2 className={sectionTitleClass}>Child Information</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className={fieldLabelClass}>First Name</p>
                {editing ? (
                  <input
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={editablePerson.children.firstName}
                    onChange={(e) => handleChange(e, 'children', 'firstName')}
                  />
                ) : (
                  <p className={fieldValueClass}>{person.children.firstName}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Last Name</p>
                {editing ? (
                  <input
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={editablePerson.children.lastName}
                    onChange={(e) => handleChange(e, 'children', 'lastName')}
                  />
                ) : (
                  <p className={fieldValueClass}>{person.children.lastName}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Class</p>
                <p className={fieldValueClass}>{person.children.class}</p>
              </div>
              <div>
                <p className={fieldLabelClass}>Birthday</p>
                {editing ? (
                  <DatePicker
                    selected={new Date(editablePerson.children.dateOfBirth)}
                    onChange={(date) => handleDateChange(date, 'children', 'dateOfBirth')}
                    dateFormat="MM/dd/yyyy"
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                ) : (
                  <p className={fieldValueClass}>{new Date(person.children.dateOfBirth).toLocaleDateString()}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Siblings</p>
                <div className={`${fieldValueClass} min-h-[42px]`}>
                  {person.children.siblings.length > 0 ? (
                    <ul className="space-y-1">
                      {person.children.siblings.map((sibling, index) => (
                        <li key={index}>
                          <button
                            onClick={() => handleSiblingClick(sibling.split(' ')[0], sibling.split(' ')[1])}
                            className="text-blue-600 hover:underline"
                          >
                            {sibling}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </div>
              </div>
              <div>
                <p className={fieldLabelClass}>Priority</p>
                <button
                  onClick={handlePriorityToggle}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 hover:bg-gray-50"
                >
                  {person.enquiry.priority ? <FaStar color="gold" size={18} /> : <FaRegStar color="gray" size={18} />}
                  <span className="text-sm text-gray-700">{person.enquiry.priority ? 'Priority' : 'Standard'}</span>
                </button>
              </div>
            </div>
          </div>

          <div className={sectionCardClass}>
            <h2 className={sectionTitleClass}>Parent Information</h2>
            <div className="space-y-4">
              <div>
                <p className={fieldLabelClass}>Parent Name</p>
                {editing ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <input
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      value={editablePerson.parent.parentFirstName}
                      onChange={(e) => handleChange(e, 'parent', 'parentFirstName')}
                    />
                    <input
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      value={editablePerson.parent.parentLastName}
                      onChange={(e) => handleChange(e, 'parent', 'parentLastName')}
                    />
                  </div>
                ) : (
                  <p className={fieldValueClass}>{person.parent.parentFirstName} {person.parent.parentLastName}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Email</p>
                {editing ? (
                  <input
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={editablePerson.parent.email}
                    onChange={(e) => handleChange(e, 'parent', 'email')}
                  />
                ) : (
                  <p className={fieldValueClass}>{person.parent.email || '-'}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Phone</p>
                {editing ? (
                  <input
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={editablePerson.parent.phone || ""}
                    onChange={(e) => handleChange(e, 'parent', 'phone')}
                  />
                ) : (
                  <p className={fieldValueClass}>{person.parent.phone || '-'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className={sectionCardClass}>
            <h2 className={sectionTitleClass}>Enquiry Information</h2>
            <div className="space-y-4">
              <div>
                <p className={fieldLabelClass}>Enquiry Date</p>
                {editing ? (
                  <DatePicker
                    selected={new Date(editablePerson.enquiry.enquiryDate)}
                    onChange={(date) => handleDateChange(date, 'enquiry', 'enquiryDate')}
                    dateFormat="MM/dd/yyyy"
                    maxDate={new Date()}
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                ) : (
                  <p className={fieldValueClass}>{formatTimestamp(person.enquiry.enquiryDate)}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Desired Enrollment Date</p>
                {editing ? (
                  <DatePicker
                    selected={new Date(editablePerson.enquiry.desiredEnrollmentDate)}
                    onChange={(date) => handleDateChange(date, 'enquiry', 'desiredEnrollmentDate')}
                    dateFormat="MM/dd/yyyy"
                    minDate={new Date()}
                    showYearDropdown
                    scrollableMonthYearDropdown
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                ) : (
                  <p className={fieldValueClass}>{formatTimestamp(person.enquiry.desiredEnrollmentDate)}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Status</p>
                {editing ? (
                  <select
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={editablePerson.enquiry.status}
                    onChange={(e) => handleChange(e, 'enquiry', 'status')}
                  >
                    <option value="Called In">Called In</option>
                    <option value="Enquired">Enquired</option>
                    <option value="Emailed">Emailed</option>
                    <option value="Toured">Toured</option>
                  </select>
                ) : (
                  <p className={fieldValueClass}>{person.enquiry.status}</p>
                )}
              </div>
              <div>
                <p className={fieldLabelClass}>Waitlist</p>
                {editing ? (
                  <select
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={editablePerson.enquiry.whichWaitlist}
                    onChange={(e) => handleChange(e, 'enquiry', 'whichWaitlist')}
                  >
                    <option value="Main">Main</option>
                    <option value="Starting">Starting</option>
                    <option value="Future">Future / Incoming</option>
                    <option value="Incoming">Incoming (Legacy)</option>
                    <option value="Declined">Declined</option>
                    <option value="Archived">Archived</option>
                    <option value="Enrolled">Enrolled</option>
                  </select>
                ) : (
                  <p className={fieldValueClass}>{person.enquiry.whichWaitlist}</p>
                )}
              </div>
            </div>
          </div>

          <div className={sectionCardClass}>
            <h2 className={sectionTitleClass}>Allergies</h2>
            <textarea
              className="min-h-[180px] w-full rounded-md border border-gray-300 px-3 py-2"
              value={editableAllergies}
              onChange={handleAllergiesChange}
            />
            <button
              className="mt-3 inline-flex items-center gap-2 rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
              onClick={handleAllergiesSave}
            >
              <FaCheck color="white" size={16}></FaCheck>
              Save Allergies
            </button>
          </div>
        </div>

        <div className={`${sectionCardClass} mt-5`}>
          <h2 className={sectionTitleClass}>Notes</h2>
          <div className="space-y-2">
            {person.children.notes.length > 0 ? (
              person.children.notes.map((note, index) => (
                <div key={index} className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div>
                    <p className="text-gray-800">{note.content}</p>
                    <p className="mt-1 text-xs text-gray-500">{formatTimestamp(note.timestamp)}</p>
                  </div>
                  <button
                    className="rounded bg-red-500 px-2 py-1 text-white hover:bg-red-600"
                    onClick={() => handleDeleteNote(index)}
                  >
                    <FaTrash color="white" size={16}></FaTrash>
                  </button>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No notes yet.</p>
            )}
          </div>
          {addingNote ? (
            <div className="mt-3">
              <textarea
                className="min-h-[110px] w-full rounded-md border border-gray-300 px-3 py-2"
                value={newNote}
                onChange={handleNewNoteChange}
              />
              <button
                className="mt-2 inline-flex items-center gap-2 rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
                onClick={handleSaveNewNote}
              >
                <FaCheck color="white" size={16}></FaCheck>
                Save Note
              </button>
            </div>
          ) : (
            <button
              className="mt-3 inline-flex items-center gap-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              onClick={handleAddNote}
            >
              <FaPlus color="white" size={16}></FaPlus>
              Add Note
            </button>
          )}
        </div>

        <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
          {editing ? (
            <div className="flex flex-wrap justify-end gap-3">
              <button
                className="inline-flex items-center gap-2 rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
                onClick={handleSaveClick}
              >
                <FaCheck color="white" size={16}></FaCheck>
                Save
              </button>
              <button
                className="inline-flex items-center gap-2 rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap justify-between gap-3">
              <button
                className="inline-flex items-center gap-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                onClick={handleEditClick}
              >
                <FaPencilAlt color="white" size={16}></FaPencilAlt>
                Edit
              </button>
              <button
                className="inline-flex items-center gap-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                onClick={onBack}
              >
                Back
              </button>
              <button
                className="inline-flex items-center gap-2 rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                onClick={handleDelete}
              >
                <FaTrash color="white" size={16}></FaTrash>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="mt-6">
        <PrintableContent person={person} />
      </div>
    </div>
  );
}

const removeUndefined = (obj) => {
  if (obj && typeof obj === 'object') {
    Object.keys(obj).forEach((key) => {
      if (obj[key] && typeof obj[key] === 'object') {
        removeUndefined(obj[key]);
      } else if (obj[key] === undefined) {
        console.log(`Removing undefined field: ${key}`);
        delete obj[key];
      }
    });
  }
  return obj;
};


export default PersonDetail;
